/**
 * How many of a product may be ordered, and in what steps.
 *
 * Torsbo sells ammunition by the cartridge but never one cartridge at a time: Jeeves carries a
 * minimum order (`th_minorderantal`, 1000 on 132 products — "Pris vid 1000st", the price is per
 * cartridge) and a pack size (`th_forpackningsantal`, 50 on 131 products). The old shop enforced
 * both in the browser and the client asked for the same here, storefront only, with no Magento
 * stock rule behind it.
 *
 * The quantity stays in UNITS everywhere — the cart, the price and Magento are all unit-based — so
 * a pack size becomes the step and the minimum is rounded UP to whole packs. The old shop counted
 * packs in the box instead; that would have put a pack count into a unit-priced cart.
 *
 * Pure so both quantity selectors and the product page share one reading, and so the arithmetic
 * is testable without a component.
 */

/** The most anyone can order of one line, product page and cart alike. */
export const MAX_QUANTITY = 10_000;

export interface QuantityRules {
  min: number;
  step: number;
  max: number;
}

/** No attributes: exactly the rules the shop had before, apart from the 10 000 ceiling. */
export const DEFAULT_QUANTITY_RULES: QuantityRules = { min: 1, step: 1, max: MAX_QUANTITY };

/**
 * A positive whole number from an attribute value, or undefined.
 *
 * Both attributes are varchar, so anything may arrive: NULL, "", "0", "-5", "12.5", "50 st". Only
 * a clean positive integer means something; everything else is treated as absent, which falls
 * back to the unrestricted behaviour rather than inventing a rule.
 */
export function parsePositiveInt(value: number | string | null | undefined): number | undefined {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return undefined;
  const parsed = Number(text);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * The rules for one product.
 *
 * - step = the pack size, or 1.
 * - min = the minimum order rounded up to whole packs, and never less than one pack.
 * - max = 10 000 rounded DOWN to whole packs, so the ceiling is itself a valid quantity.
 *
 * A minimum above the ceiling (not in the catalogue today) lifts the ceiling to the minimum rather
 * than leaving a product nobody can order.
 */
export function quantityRules({
  minOrder,
  packSize,
}: {
  minOrder?: number | string | null;
  packSize?: number | string | null;
}): QuantityRules {
  const step = parsePositiveInt(packSize) ?? 1;
  const minimum = parsePositiveInt(minOrder) ?? 1;
  const min = Math.max(step, Math.ceil(minimum / step) * step);
  const max = Math.max(min, Math.floor(MAX_QUANTITY / step) * step);
  return { min, step, max };
}

/**
 * The nearest valid quantity at or above `value`: rounded up to a whole pack, raised to the
 * minimum, capped at the maximum. A non-number becomes the minimum.
 */
export function clampQuantity(value: number, rules: QuantityRules): number {
  if (!Number.isFinite(value)) return rules.min;
  const rounded = Math.ceil(value / rules.step) * rules.step;
  return Math.min(rules.max, Math.max(rules.min, rounded));
}

/**
 * One pack up from `value`. A line sitting between packs (added before these rules existed, say)
 * moves to the next whole pack rather than keeping its odd remainder.
 */
export function stepUp(value: number, rules: QuantityRules): number {
  return clampQuantity((Math.floor(value / rules.step) + 1) * rules.step, rules);
}

/** One pack down from `value`, never below the minimum. Same off-grid reading as `stepUp`. */
export function stepDown(value: number, rules: QuantityRules): number {
  return clampQuantity((Math.ceil(value / rules.step) - 1) * rules.step, rules);
}
